import React, { useEffect, useMemo, useState } from 'react';
import { Delete, Star } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { CustomeTextField } from '../../generic/CustomeTextField';
import { PostcodeSearchByID } from '../../generic/PostcodeSearchById';
import { IFederalState } from '../../Interfaces/IAemter';
import { ILocation, locationSchema } from '../../Interfaces/ILocation';
import {
  useCreateLocation,
  useDeleteLocation,
  useSetDefaultLocation,
  useUpdateLocation,
} from '../../hooks/reactQuerys/LocationQueries';
import {
  useCreateRoom,
  useDeleteRoom,
  useGetRooms,
  useUpdateRoom,
} from '../../hooks/reactQuerys/RoomQueries';
import { useAlert } from '../../context/AlertContext';
import { RoomManager, IRoomDraftItem } from './RoomManager';

interface IProps {
  currentLocation: ILocation;
  federalStates: IFederalState[];
  onClose: () => void;
  onSaved: (location: ILocation) => void;
  onDeleted: (idLocation: number) => void;
}

const mapLocationForSubmit = (location: ILocation) => ({
  ...location,
  StreetAdditional: location.StreetAdditional.trim() || '',
  Comment: location.Comment.trim() || '',
});

export const LocationEdit: React.FC<IProps> = ({
  currentLocation,
  federalStates,
  onClose,
  onSaved,
  onDeleted,
}) => {
  const { showAlert } = useAlert();
  const [editLocation, setEditLocation] = useState<ILocation>(currentLocation);
  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] =
    useState(false);

  const { createLocation, isPendingCreateLocation } = useCreateLocation();
  const { updateLocation, isPendingUpdateLocation } = useUpdateLocation();
  const { deleteLocation, isPendingDeleteLocation } = useDeleteLocation();
  const { setDefaultLocation, isPendingSetDefaultLocation } =
    useSetDefaultLocation();

  // Raum-Entwürfe: lokal bearbeitbar, persistiert erst beim Speichern.
  const { rooms, isLoadingRooms } = useGetRooms();
  const { createRoom } = useCreateRoom();
  const { updateRoom } = useUpdateRoom();
  const { deleteRoom } = useDeleteRoom();

  const originalRooms = useMemo<IRoomDraftItem[]>(
    () =>
      rooms
        .filter(r => r.Location_idLocation === currentLocation.idLocation)
        .map(r => ({
          idRoom: r.idRoom,
          Name: r.Name,
          Capacity: r.Capacity ?? null,
        })),
    [rooms, currentLocation.idLocation]
  );

  const [roomDrafts, setRoomDrafts] = useState<IRoomDraftItem[]>([]);
  const [roomsInitialised, setRoomsInitialised] = useState(false);
  // Einmalig aus den Server-Räumen initialisieren, sobald geladen.
  useEffect(() => {
    if (!roomsInitialised && !isLoadingRooms) {
      setRoomDrafts(originalRooms);
      setRoomsInitialised(true);
    }
  }, [isLoadingRooms, roomsInitialised, originalRooms]);

  const isPending =
    isPendingCreateLocation ||
    isPendingUpdateLocation ||
    isPendingDeleteLocation ||
    isPendingSetDefaultLocation;

  const handleSetDefault = async () => {
    try {
      await setDefaultLocation(editLocation.idLocation);
      setEditLocation(current => ({ ...current, isDefault: true }));
      showAlert({
        text: 'Standort als Standard festgelegt.',
        severity: 'success',
      });
    } catch {
      showAlert({
        text: 'Standardstandort konnte nicht gesetzt werden.',
        severity: 'error',
      });
    }
  };

  const selectedFederalState = useMemo(
    () =>
      federalStates.find(
        federalState =>
          federalState.idFederalState === editLocation.idFederalState
      ) ?? null,
    [editLocation.idFederalState, federalStates]
  );

  const isSaveDisabled =
    editLocation.Location.trim() === '' ||
    editLocation.idFederalState < 1 ||
    editLocation.idPostcode === null ||
    editLocation.idPostcode < 1 ||
    editLocation.Street.trim() === '' ||
    editLocation.StreetNr.trim() === '';

  const handleSave = async () => {
    try {
      const parsedLocation = locationSchema.parse(editLocation);
      const payload = mapLocationForSubmit(parsedLocation);
      const { idLocation: _idLocation, ...createPayload } = payload;

      const savedLocation =
        parsedLocation.idLocation < 0
          ? await createLocation(createPayload)
          : await updateLocation(payload);

      const savedParsed = locationSchema.parse(savedLocation);
      const targetId = savedParsed.idLocation;

      // Raum-Diffs gegen den Server-Stand anwenden (erst jetzt persistieren).
      const draftIds = new Set(
        roomDrafts.filter(r => r.idRoom > 0).map(r => r.idRoom)
      );
      // Entfernte Räume löschen.
      await Promise.all(
        originalRooms
          .filter(r => !draftIds.has(r.idRoom))
          .map(r => deleteRoom(r.idRoom))
      );
      // Neue anlegen, geänderte aktualisieren.
      await Promise.all(
        roomDrafts
          .filter(r => r.Name.trim() !== '')
          .map(r => {
            if (r.idRoom < 0) {
              return createRoom({
                Location_idLocation: targetId,
                Name: r.Name.trim(),
                Capacity: r.Capacity,
                Location: '',
              });
            }
            const orig = originalRooms.find(o => o.idRoom === r.idRoom);
            if (
              orig &&
              (orig.Name !== r.Name.trim() || orig.Capacity !== r.Capacity)
            ) {
              return updateRoom({
                idRoom: r.idRoom,
                Location_idLocation: targetId,
                Name: r.Name.trim(),
                Capacity: r.Capacity,
                Location: '',
              });
            }
            return Promise.resolve(undefined);
          })
      );

      onSaved(savedParsed);
      showAlert({
        text: 'Standort erfolgreich gespeichert.',
        severity: 'success',
      });
    } catch {
      showAlert({
        text: 'Standort konnte nicht gespeichert werden.',
        severity: 'error',
      });
    }
  };

  const handleDelete = async () => {
    if (editLocation.idLocation < 1) {
      onClose();
      return;
    }

    try {
      await deleteLocation(editLocation.idLocation);
      onDeleted(editLocation.idLocation);
      showAlert({
        text: 'Standort erfolgreich gelöscht.',
        severity: 'success',
      });
    } catch {
      showAlert({
        text: 'Standort konnte nicht gelöscht werden.',
        severity: 'error',
      });
    } finally {
      setIsOpenDeleteConfirmation(false);
    }
  };

  return (
    <>
      <Dialog
        open={isOpenDeleteConfirmation}
        onClose={() => setIsOpenDeleteConfirmation(false)}
      >
        <DialogTitle>Standort löschen</DialogTitle>
        <DialogContent>
          Möchten Sie den Standort &quot;{editLocation.Location || ''}&quot;
          wirklich löschen?
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            onClick={handleDelete}
            disabled={isPendingDeleteLocation}
            color='error'
          >
            {isPendingDeleteLocation ? (
              <Box display='flex' alignItems='center' gap={1}>
                <span>Löschen</span>
                <CircularProgress size={20} />
              </Box>
            ) : (
              'Löschen'
            )}
          </Button>
          <Button
            variant='contained'
            onClick={() => setIsOpenDeleteConfirmation(false)}
            disabled={isPendingDeleteLocation}
          >
            Abbruch
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant='h6'>
          {editLocation.idLocation < 0 ? 'Neuer Standort' : 'Standort'}
        </Typography>
        {editLocation.idLocation > 0 && (
          <IconButton
            size='small'
            onClick={() => setIsOpenDeleteConfirmation(true)}
            disabled={isPending}
          >
            <Delete />
          </IconButton>
        )}
      </Box>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <CustomeTextField
            attr='Location'
            object={editLocation}
            label='Standortname'
            setObject={setEditLocation}
            type='string'
            required
            maxLength='md'
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={federalStates}
            value={selectedFederalState}
            getOptionLabel={option => option.FederalState}
            onChange={(_, newValue) => {
              setEditLocation(current => ({
                ...current,
                idFederalState: newValue?.idFederalState ?? 0,
                FederalState: newValue?.FederalState ?? '',
                idPostcode:
                  newValue?.idFederalState === current.idFederalState
                    ? current.idPostcode
                    : null,
                Postcode:
                  newValue?.idFederalState === current.idFederalState
                    ? current.Postcode
                    : '',
                City:
                  newValue?.idFederalState === current.idFederalState
                    ? current.City
                    : '',
              }));
            }}
            isOptionEqualToValue={(option, value) =>
              option.idFederalState === value.idFederalState
            }
            renderInput={params => (
              <TextField {...params} label='Bundesland' required size='small' />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <PostcodeSearchByID
            key={`location-postcode-${editLocation.idPostcode ?? 'empty'}`}
            idPostcode={editLocation.idPostcode}
            setIdPostcode={(idPostcode: number | null) =>
              setEditLocation(current => ({
                ...current,
                idPostcode,
              }))
            }
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <CustomeTextField
            attr='Street'
            object={editLocation}
            label='Straße'
            setObject={setEditLocation}
            type='string'
            required
            maxLength='md'
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <CustomeTextField
            attr='StreetNr'
            object={editLocation}
            label='Hausnummer'
            setObject={setEditLocation}
            type='string'
            required
            maxLength={20}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <CustomeTextField
            attr='StreetAdditional'
            object={editLocation}
            label='Zusatz'
            setObject={setEditLocation}
            type='string'
            maxLength='md'
          />
        </Grid>

        <Grid item xs={12}>
          <CustomeTextField
            attr='Comment'
            object={editLocation}
            label='Anmerkung für Extern (Wegbeschreibung)'
            setObject={setEditLocation}
            type='string'
            rows={4}
            maxLength={500}
          />
        </Grid>
      </Grid>

      {editLocation.idLocation > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            {editLocation.isDefault ? (
              <Chip
                color='warning'
                icon={<Star fontSize='small' />}
                label='Standardstandort'
              />
            ) : (
              <Button
                variant='outlined'
                startIcon={<Star />}
                onClick={handleSetDefault}
                disabled={isPending}
              >
                Als Standardstandort setzen
              </Button>
            )}
            <Typography variant='caption' color='text.secondary'>
              {editLocation.isDefault
                ? 'Zum Wechseln einen anderen Standort als Standard setzen.'
                : 'Es gibt immer genau einen Standardstandort.'}
            </Typography>
          </Box>
        </>
      )}

      <Divider sx={{ my: 2 }} />
      <RoomManager rooms={roomDrafts} onChange={setRoomDrafts} />

      {editLocation.idLocation < 0 && (
        <Alert severity='info' sx={{ mt: 1 }}>
          Der Standardstandort kann festgelegt werden, sobald der Standort
          gespeichert wurde.
        </Alert>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant='outlined' onClick={onClose} disabled={isPending}>
          Abbruch
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={isPending || isSaveDisabled}
        >
          {isPending ? (
            <Box display='flex' alignItems='center' gap={1}>
              <span>Speichern</span>
              <CircularProgress size={18} />
            </Box>
          ) : (
            'Speichern'
          )}
        </Button>
      </Box>
    </>
  );
};
